import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../contexts/AuthContext';

const SYM = { INR: '₹', USD: '$' };

// ── App-wide gradient ─────────────────────────────────────────────────────────
const GRAD = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

const BRAND = {
  primary:    '#004AAD',
  secondary:  '#0082FB',
  grad:       GRAD,
  textBlue:   'rgb(0, 130, 251)',
  borderBlue: 'rgb(0, 130, 251)',
};

const STYLES = `
  @font-face {
    font-family: 'Sansation';
    src: local('Sansation Regular'), local('Sansation'), local('Sansation-Regular');
    font-weight: 400;
    font-style: normal;
  }
  @font-face {
    font-family: 'Sansation';
    src: local('Sansation Bold'), local('Sansation-Bold');
    font-weight: 700;
    font-style: normal;
  }

  .ats-root * { box-sizing: border-box; }

  .ats-root {
    font-family: inherit;
    width: 100%;
    background: #f8fafc;
    border-radius: 20px;
    padding: 24px 20px 28px;
  }

  /* ── Header ── */
  .ats-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 22px;
  }
  .ats-title {
    margin: 0;
    font-size: clamp(16px, 4vw, 19px);
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
    font-family: 'Sansation', sans-serif;
  }

  /* ── Currency Toggle ── */
  .ats-toggle {
    display: inline-flex;
    align-items: center;
    background: #f1f5f9;
    border-radius: 100px;
    padding: 3px;
    border: 1px solid #e2e8f0;
    flex-shrink: 0;
  }
  .ats-toggle-btn {
    padding: 5px 16px;
    border-radius: 100px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.15s;
    font-family: inherit;
    white-space: nowrap;
  }
  .ats-toggle-btn.active {
    background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
    color: #fff;
    box-shadow: 0 1px 6px rgba(0,74,173,0.35);
  }
  .ats-toggle-btn.inactive {
    background: transparent;
    color: #64748b;
  }

  /* ── Cards grid ── */
  .ats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    justify-content: center;
  }

  /* ── Single card ── */
  .ats-card {
    border-radius: 18px;
    padding: 24px 20px 20px;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    overflow: visible;
  }
  .ats-card:hover { transform: translateY(-3px); }

  .ats-card-free {
    background: #ffffff;
    border: 1.5px solid rgba(0,130,251,0.25);
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .ats-card-free.selected {
    border: 2px solid #004AAD;
    box-shadow: 0 6px 24px rgba(0,74,173,0.14);
  }

  .ats-card-paid {
    background: linear-gradient(160deg, rgba(0,130,251,0.06) 0%, rgba(0,74,173,0.09) 100%);
    border: 1.5px solid rgb(0,130,251);
    box-shadow: 0 2px 10px rgba(0,130,251,0.1);
  }
  .ats-card-paid.selected {
    border: 2px solid #004AAD;
    box-shadow: 0 6px 24px rgba(0,74,173,0.16);
  }

  /* ── Selected dot — uses updated gradient ── */
  .ats-dot {
    position: absolute;
    top: 14px; right: 14px;
    width: 22px; height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 6px rgba(0,74,173,0.40);
  }

  /* ── Launch badge — intentional amber/orange, do NOT change ── */
  .ats-launch-badge {
    position: absolute;
    top: -13px; left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    padding: 5px 12px;
    border-radius: 100px;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(245,158,11,0.4);
    display: flex; align-items: center; gap: 4px;
    font-family: inherit;
  }

  /* ── Card label ── */
  .ats-label {
    margin: 0 0 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.09em;
    color: rgb(0,130,251);
    text-transform: uppercase;
    font-family: inherit;
  }

  /* ── Price row ── */
  .ats-strikethrough {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 6px;
  }
  .ats-strikethrough-price {
    font-size: 14px; font-weight: 500; color: #94a3b8;
    text-decoration: line-through;
  }
  .ats-discount-badge {
    background: #fef3c7; border: 1px solid #fde68a;
    color: #92400e; font-size: 10px; font-weight: 700;
    padding: 2px 7px; border-radius: 6px; font-family: inherit;
  }
  .ats-price-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
  }
  .ats-price-big {
    font-size: clamp(34px, 7vw, 44px);
    font-weight: 700; color: #0f172a;
    line-height: 1; letter-spacing: -0.02em;
    font-family: 'Sansation', sans-serif;
  }
  .ats-price-suffix {
    font-size: 14px; font-weight: 400; color: #94a3b8; margin-left: 5px;
  }
  .ats-price-sub {
    font-size: 12px; color: #64748b; margin: 0; font-weight: 400;
  }
  .ats-gst-badge {
    font-size: 10px;
    font-weight: 500;
    color: #10b981;
    background: rgba(16, 185, 129, 0.1);
    padding: 2px 8px;
    border-radius: 100px;
    display: inline-block;
  }

  /* ── Badge pill ── */
  .ats-pill {
    display: inline-flex; align-items: center; gap: 6px;
    border-radius: 100px; padding: 5px 12px;
    margin-top: 10px; margin-bottom: 18px;
    width: fit-content;
    font-family: inherit;
  }
  .ats-pill-blue  { background: rgba(0,130,251,0.1); }
  .ats-pill-amber { background: rgba(245,158,11,0.12); border: 1px solid rgba(245,158,11,0.3); }
  .ats-pill span  { font-size: 12px; font-weight: 600; }
  .ats-pill-blue span  { color: rgb(0,130,251); }
  .ats-pill-amber span { color: #b45309; }

  /* ── Features ── */
  .ats-features { flex: 1; margin-bottom: 20px; }
  .ats-feat {
    display: flex; align-items: flex-start; gap: 10px;
    margin-bottom: 12px;
  }
  .ats-feat span {
    font-size: 13px; font-weight: 400;
    color: #1e293b; line-height: 1.45;
    font-family: inherit;
  }
  .ats-feat svg { flex-shrink: 0; margin-top: 1px; }

  /* ── Buttons ── */
  .ats-btn-ghost {
    width: 100%; padding: 13px 0; border-radius: 100px;
    background: #fff; border: 1.5px solid rgb(0,130,251);
    color: rgb(0,130,251); font-size: 14px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: background 0.15s;
  }
  .ats-btn-ghost:hover:not(:disabled) { background: rgba(0,130,251,0.05); }
  .ats-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

  .ats-btn-solid {
    width: 100%; padding: 14px 0; border-radius: 100px;
    background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
    color: #fff; border: none; font-size: 14px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    box-shadow: 0 4px 14px rgba(0,74,173,0.40);
    transition: filter 0.15s, transform 0.15s;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
  .ats-btn-solid:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
  .ats-btn-solid:active:not(:disabled) { filter: brightness(0.96); transform: translateY(0); }
  .ats-btn-solid:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }

  .ats-cta-sub {
    margin: 6px 0 0; font-size: 11px; color: #94a3b8;
    text-align: center; font-family: inherit;
  }
  .ats-cta-sub-italic {
    margin: 6px 0 0; font-size: 11px; color: #64748b;
    text-align: center; font-style: italic; font-family: inherit;
  }

  /* ─────────── MOBILE ─────────── */
  @media (max-width: 600px) {
    .ats-root { padding: 18px 14px 22px; border-radius: 16px; }
    .ats-grid { grid-template-columns: 1fr; gap: 24px; }
    .ats-card-paid { margin-top: 10px; }
    .ats-card { padding: 22px 18px 18px; }
    .ats-launch-badge { font-size: 9.5px; padding: 4px 11px; }
    .ats-price-big { font-size: 38px; }
    .ats-feat span { font-size: 13px; }
    .ats-btn-ghost, .ats-btn-solid { font-size: 14px; padding: 13px 0; }
    .ats-header { flex-direction: column; align-items: flex-start; }
  }

  @media (max-width: 360px) {
    .ats-price-big { font-size: 32px; }
    .ats-root { padding: 14px 10px 18px; }
  }
`;

// ── Icons ─────────────────────────────────────────────────────────────────────
const Check = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M4 10.5L8 14.5L16 6.5" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BoltIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="#f59e0b"/>
  </svg>
);

// Helper function to calculate discount percentage
const getDiscountPercentage = (original, offer) => {
  if (!original || !offer || original === 0) return 0;
  return Math.round(((original - offer) / original) * 100);
};

// Helper function to calculate price with GST (only for INR)
const calculatePriceWithGST = (price, gstPercentage, currency) => {
  // Only apply GST for INR currency
  return price;
  // if (currency !== 'INR') return price;
  // if (!gstPercentage || gstPercentage <= 0) return price;
  // return price + (price * gstPercentage / 100);
};
 
// ─────────────────────────────────────────────────────────────────────────────
const AuditTypeSelector = ({ selectedType, onTypeChange, onSubmit, isSubmitting = false,
  currency,
  onCurrencyChange }) => {

  const { user, getClientForRole } = useAuth();
  const [hasFreeAudit, setHasFreeAudit] = useState(false);

  useEffect(() => {
    const checkSubmissions = async () => {
      if (!user?.id) return;
      try {
        const supabase = getClientForRole('customer');
        const { data: submissionData, error: submissionError } = await supabase
          .from('deck_submissions')
          .select('audit_type')
          .eq('user_id', user.id);
        
        if (submissionError) throw submissionError;
        
        const hasFree = submissionData.some(sub => sub.audit_type === 'free');
        setHasFreeAudit(hasFree);
        
        if (hasFree) {
          onTypeChange('paid');
        }
      } catch (err) {
        console.error('Error checking submissions:', err);
      }
    };
    checkSubmissions();
  }, [user, getClientForRole]);

  const handleChange = (newCurrency) => {
  onCurrencyChange(newCurrency); 
  };
  const price = useSelector(state => state.pricing);
 
  const sym = SYM[currency];
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  
  // Safely access pricing data from your structure
  const paidData = price?.paid?.[currency] || { original: 299, offer: 75 };
  const orig = paidData.original || 299;
  const offer = paidData.offer || 75;
  
  // Get GST percentage only for INR (from INR data, even when showing USD we don't use it)
  const gstPercentage = currency === 'INR' ? (price?.paid?.INR?.gst || null) : null;
  
  // Calculate price with GST for display (only for INR)
  const finalOfferPrice = calculatePriceWithGST(offer, gstPercentage, currency);
  const finalOriginalPrice = calculatePriceWithGST(orig, gstPercentage, currency);
  
  // Calculate discount percentage dynamically
  const discountPercent = getDiscountPercentage(orig, offer);
  
  // Get badge text from your data structure
  const badgeText = price?.paid?.badge || 'LAUNCH OFFER';
  const bestFor = price?.paid?.best || 'Delivered within 72 Hours';
  
  // Get features (if available from DB, otherwise use defaults)
  const freeFeats = price?.free?.features?.length > 0 
    ? price.free.features 
    : [
        'Basic DecX Score (1 Deck)',
        '5 Dimension Breakdown',
        'Top 3 Slides Analysed',
        'Top 5 Issues Identified',
        '24-Hour Delivery',
      ];
  
  const paidFeats = price?.paid?.features?.length > 0 
    ? price.paid.features 
    : [
        'AI-powered analysis',
        'Human expert review',
        'Detailed feedback report',
        'Priority processing',
        'Actionable recommendations',
      ];

  const handleFreeClick = (e) => { 
    e.stopPropagation(); 
    onTypeChange('free'); 
    onSubmit?.('free'); 
  };
  
  const handlePaidClick = (e) => { 
    e.stopPropagation(); 
    onTypeChange('paid'); 
    onSubmit?.('paid'); 
  };

  const SelectedDot = () => (
    <div className="ats-dot">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <polyline points="20 6 9 17 4 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );

  return (
    <div className="ats-root">
      <style>{STYLES}</style>

      {/* ── Header ── */}
      <div className="ats-header">
        <h3 className="ats-title">Select Audit Type</h3>

        {/* Currency Toggle */}
        <div className="ats-toggle">
          {['INR', 'USD'].map(c => (
            <button
              key={c}
              onClick={() => handleChange(c)}
              className={`ats-toggle-btn ${currency === c ? 'active' : 'inactive'}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* ── Cards ── */}
      <div className="ats-grid" style={hasFreeAudit ? { gridTemplateColumns: 'minmax(auto, 400px)', margin: '0 auto' } : {}}>

        {/* FREE CARD */}
        {!hasFreeAudit && (
          <div
            className={`ats-card ats-card-free${selectedType === 'free' ? ' selected' : ''}`}
            onClick={() => onTypeChange('free')}
          >
            {selectedType === 'free' && <SelectedDot />}

            <p className="ats-label">FREE</p>

            <div className="ats-price-row">
              <span className="ats-price-big">{sym}0</span>
            </div>

            <div className="ats-features">
              {freeFeats.map((f, i) => (
                <div key={i} className="ats-feat">
                  <Check />
                  <span>{f}</span>
                </div>
              ))}
            </div>

            <button
              className="ats-btn-ghost"
              disabled={isSubmitting}
              onClick={handleFreeClick}
            >
              {isSubmitting && selectedType === 'free' ? 'Submitting…' : 'Submit for Analysis'}
            </button>
            <p className="ats-cta-sub">No credit card required</p>
          </div>
        )}

        {/* PAID CARD */}
        <div
          className={`ats-card ats-card-paid${selectedType === 'paid' ? ' selected' : ''}`}
          onClick={() => onTypeChange('paid')}
        >
          <div className="ats-launch-badge">🚀 {badgeText} — {discountPercent}% OFF</div>

          {selectedType === 'paid' && <SelectedDot />}

          <p className="ats-label">PAID DECK AUDIT</p>

          {/* Strikethrough price */}
          <div className="ats-strikethrough">
            <span className="ats-strikethrough-price">{sym}{finalOriginalPrice.toLocaleString(locale)}</span>
            <span className="ats-discount-badge">{discountPercent}% OFF</span>
          </div>

          {/* Offer price */}
          <div className="ats-price-row">
            <span className="ats-price-big">{sym}{finalOfferPrice.toLocaleString(locale)}</span>
            {currency === 'INR' && gstPercentage && gstPercentage > 0 && (
              <span className="ats-gst-badge">+ {gstPercentage}% GST</span>
            )}
          </div>

          {/* AI + Human badge */}
          <div className="ats-pill ats-pill-amber">
            <BoltIcon />
            <span>AI + Human Powered</span>
          </div>

          {/* Features */}
          <div className="ats-features">
            {paidFeats.map((f, i) => (
              <div key={i} className="ats-feat">
                <Check />
                <span>{f}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            className="ats-btn-solid"
            disabled={isSubmitting}
            onClick={handlePaidClick}
          >
            {isSubmitting && selectedType === 'paid' ? 'Processing…' : 'Proceed to Payment'}
          </button>
          
          {/* Best for / delivery info */}
          <p className="ats-cta-sub-italic">{bestFor}</p>
        </div>

      </div>
    </div>
  );
};

export default AuditTypeSelector;